export type BeRegionId = "vlaanderen" | "wallonie" | "brussel";

export interface BeRegion {
  id: BeRegionId;
  name: string;
}

export const BE_REGIONS: BeRegion[] = [
  { id: "vlaanderen", name: "Vlaanderen" },
  { id: "wallonie", name: "Wallonië" },
  { id: "brussel", name: "Brussel" },
];

export const BE_DEFAULT_REGION: BeRegionId = "vlaanderen";

// Alle waarden in €/kWh, exclusief btw, residentieel afnameprofiel
// (niet-zakelijk, laagste jaarlijkse verbruiksschijf 0-3 MWh).
// Bronnen geraadpleegd via deep-research (25 claims adversarieel geverifieerd) op 2026-07-13.

// Bijzondere accijns: geldig t/m 31 juli 2026, hoge zekerheid — Staatsblad
// numac 2023030776, 1-op-1 bevestigd op twee onafhankelijke officiële
// tariefkaarten van juli 2026: Octa+
// https://files.octaplus.be/tariffs/E_OCTA_DYNAMIC_RE_VL_NL.pdf
// ("Bijzondere accijns 5,0329 c€/kWh incl. btw") en DATS 24
// https://dats24.be/nl/particulier/energie/tariefkaart ("5,03288 c€/kWh incl.
// btw"); 5,03288 / 1,06 = 4,748 c€/kWh.
const BE_ACCIJNS_BEFORE = 0.04748;
// Vanaf 1 augustus 2026: hoge zekerheid — bevestigd op 2026-08-05 tegen zes
// onafhankelijke officiële tariefkaarten van augustus 2026, die alle
// 4,876 c€/kWh incl. btw als één vlak tarief voor alle verbruiksschijven
// vermelden (4,876 / 1,06 = 4,600 c€/kWh excl. btw): Engie DYNAMIC
// ("Federale Accijns — Alle verbruiken 4,87600"), Luminus Dynamic Online,
// Mega Dynamic, TotalEnergies myDynamic, Energy Knights Optima Online,
// Frank Energie en Eneco Zon & Wind Dynamisch. Post-switch nogmaals bevestigd
// op de Ecopower-kaart 'Dynamische Burgerstroom' vanaf 1 augustus 2026
// (bijzondere accijns particulier 0,04600 €/kWh excl. btw = 4,876 c€/kWh incl.
// btw, https://ecopower.be/groene-stroom/dynamische-burgerstroom, geraadpleegd
// 2026-08-13). Wettelijke basis: Programmawet 28 mei 2026 (Staatsblad 1 juni
// 2026, art. 36-44); de primaire wettekst zelf is niet rechtstreeks
// geraadpleegd, maar is als enige onderbouwing overbodig geworden.
// Let op: Octa+, Bolt en Power2You drukten op hun augustuskaart nog het oude
// degressieve tarief af (5,0329 c€/kWh) — die kaarten lopen achter, niet dit
// bestand.
const BE_ACCIJNS_FROM = 0.046;
export const BE_ACCIJNS_CHANGE_DATE = new Date(Date.UTC(2026, 7, 1));

// Energiebijdrage: geldig t/m 31 juli 2026, hoge zekerheid — zelfde twee
// tariefkaarten als hierboven: "Energiebijdrage 0,20417 c€/kWh incl. btw" / 1,06
// = 0,19261 c€/kWh.
const BE_ENERGIEBIJDRAGE_BEFORE = 0.0019261;
// Vanaf 1 augustus 2026: afgeschaft (€0) — hoge zekerheid, bevestigd op
// 2026-08-05. De Mega-tariefkaart van augustus 2026 zegt het met zoveel
// woorden: "De energiebijdrage is vanaf 1 augustus 2026 vastgesteld op 0 en het
// bedrag ervan is opgenomen in de bijzondere accijns overeenkomstig de geldende
// regelgeving", en de Eneco-kaart herhaalt dat ("Vanaf 1 augustus 2026 bedraagt
// de energiebijdrage 0 EUR/MWh"). De kaarten van Luminus ("Bijdrage op de
// energie: -") en Energy Knights (0,0000) tonen hetzelfde, en Engie en Frank
// Energie voeren de regel helemaal niet meer op. Post-switch bevestigd op de
// Ecopower-kaart vanaf 1 augustus 2026 (particulier €0,00/kWh, geraadpleegd
// 2026-08-13). Zelfde Programmawet als bij de bijzondere accijns hierboven.
const BE_ENERGIEBIJDRAGE_FROM = 0;

// Regionale heffingen: enkel per-kWh-toeslagen die via het energiecomponent van
// de leverancier worden aangerekend; nettarieven/capaciteitstarief vallen erbuiten.
const REGIONALE_HEFFINGEN: Record<BeRegionId, number> = {
  // Vlaanderen: bevestigd 0 — de "bijdrage Energiefonds" is een vast bedrag
  // per aansluiting per maand (€0,00/maand voor een woning), geen c€/kWh-heffing.
  // Bronnen: DATS 24 tariefkaart,
  // https://www.vlaamsenutsregulator.be/nl/heffingen , geraadpleegd 2026-07-13.
  vlaanderen: 0,
  // Wallonië: "aansluitingsvergoeding" (redevance de raccordement), 0,075
  // c€/kWh, aangerekend via de leverancier, arrêté 19/06/2003, Waals
  // Energiefonds. Bronnen: DATS 24 tariefkaart van juli 2026
  // ("Aansluitingsvergoeding Wallonië 0,07500 c€/kWh — Niet aan btw
  // onderworpen"), SPW energie.wallonie.be, geraadpleegd 2026-07-13.
  // Modelleeropmerking: deze heffing is NIET aan btw onderworpen, maar ons
  // prijsmodel past btw toe over de volledige belastingcomponent; de
  // resulterende fout is 0,00075 × 0,06 = €0,000045/kWh — verwaarloosbaar en
  // geaccepteerd.
  wallonie: 0.00075,
  // Brussel: nog niet geverifieerd — geen per-kWh-heffing via de leverancier
  // teruggevonden op de onderzochte VL/WAL-tariefkaarten, maar Brusselse
  // tariefkaarten zelf zijn niet onderzocht. Blijft voorlopig 0 tot verificatie.
  brussel: 0,
};

export const isBeRegionId = (value: string): value is BeRegionId =>
  BE_REGIONS.some((region) => region.id === value);

export const getBeEnergyTaxRate = (
  date: Date = new Date(),
  regionId: BeRegionId = BE_DEFAULT_REGION
): number => {
  const [accijns, energiebijdrage] =
    date >= BE_ACCIJNS_CHANGE_DATE
      ? [BE_ACCIJNS_FROM, BE_ENERGIEBIJDRAGE_FROM]
      : [BE_ACCIJNS_BEFORE, BE_ENERGIEBIJDRAGE_BEFORE];

  return accijns + energiebijdrage + REGIONALE_HEFFINGEN[regionId];
};
